import * as React from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import ThemeProvider from "../contexts/ThemeContext";
import PATH from "../paths";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { isAuthenticatedSelector } from "../redux/slice/selectors";
import { login, me } from "../redux/slice/auth";
import store from "../redux/store";
import { authRequest } from "../api/AuthRequest";
import { AxiosError } from "axios";

interface PendingTwoFactorState {
  email?: string;
  expiresAt: string;
  twoFactorToken: string;
}

export default function Login() {
  const dispatch = useDispatch<typeof store.dispatch>();
  const isAuthenticated = useSelector(isAuthenticatedSelector);

  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [verificationCode, setVerificationCode] = React.useState("");
  const [pendingTwoFactor, setPendingTwoFactor] =
    React.useState<PendingTwoFactorState | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const getErrorMessage = (
    error: unknown,
    isTwoFactorStep: boolean
  ): string => {
    if (error instanceof AxiosError) {
      const message = error.response?.data?.message;

      if (typeof message === "string") {
        return message;
      }

      if (Array.isArray(message) && message.length) {
        return message.join(", ");
      }
    }

    return isTwoFactorStep
      ? "A apărut o eroare la verificarea codului. Reîncearcă autentificarea."
      : "Numele de utilizator sau parola sunt incorecte!";
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsSubmitting(true);

    try {
      if (pendingTwoFactor) {
        await authRequest.verifyEmailTwoFactor({
          username,
          code: verificationCode,
          twoFactorToken: pendingTwoFactor.twoFactorToken,
        });
      } else {
        const response = await dispatch(
          login({
            username,
            password,
          })
        ).unwrap();

        if (response?.requiresTwoFactor) {
          setPendingTwoFactor({
            email: response.email,
            expiresAt: response.expiresAt,
            twoFactorToken: response.twoFactorToken,
          });
          setVerificationCode("");
          return;
        }
      }

      await dispatch(me()).unwrap();
    } catch (error) {
      alert(getErrorMessage(error, Boolean(pendingTwoFactor)));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to={PATH.TRIP.LIST} />;
  }

  return (
    <ThemeProvider>
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="70vh"
        >
          <Box>
            <Grid
              container
              direction="column"
              justifyContent="center"
              alignItems="center"
              // style={{ minHeight: "80vh" }}
            >
              <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
                <LockOutlinedIcon />
              </Avatar>
              <Typography component="h1" variant="h5">
                eTMS
              </Typography>
              <Box
                component="form"
                onSubmit={(event) => {
                  handleSubmit(event);
                }}
                noValidate
                sx={{ mt: 1 }}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Nume utilizator"
                  name="username"
                  autoComplete="username"
                  autoFocus={!pendingTwoFactor}
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  disabled={Boolean(pendingTwoFactor)}
                />
                {!pendingTwoFactor ? (
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Parolă"
                    type="password"
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                ) : (
                  <>
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      {pendingTwoFactor.email
                        ? `Am trimis codul de autentificare la ${pendingTwoFactor.email}.`
                        : "Am trimis codul de autentificare pe email."}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Introdu codul de 6 cifre pentru a continua.
                    </Typography>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="verificationCode"
                      label="Cod verificare"
                      id="verificationCode"
                      autoComplete="one-time-code"
                      value={verificationCode}
                      onChange={(event) =>
                        setVerificationCode(event.target.value)
                      }
                    />
                  </>
                )}
                {/*<Grid*/}
                {/*  container*/}
                {/*  justifyContent="space-between"*/}
                {/*  alignItems="center"*/}
                {/*>*/}
                {/*  <Grid item>*/}
                {/*    <FormControlLabel*/}
                {/*      control={<Checkbox value="remember" color="primary" />}*/}
                {/*      label="Ține-mă minte"*/}
                {/*    />*/}
                {/*  </Grid>*/}
                {/*  <Grid item>*/}
                {/*    <Typography color="primary">*/}
                {/*      <Link color="inherit" href="https://mui.com/">*/}
                {/*        Resetează parola*/}
                {/*      </Link>*/}
                {/*    </Typography>*/}
                {/*  </Grid>*/}
                {/*</Grid>*/}
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {pendingTwoFactor && (
                    <Grid item xs={12}>
                      <Button
                        type="button"
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setPendingTwoFactor(null);
                          setVerificationCode("");
                        }}
                        disabled={isSubmitting}
                      >
                        Înapoi
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{ mt: pendingTwoFactor ? 0 : 1 }}
                      disabled={isSubmitting}
                    >
                      {pendingTwoFactor
                        ? "Verifică codul"
                        : "Autentificare"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
}
